import { Box, Button, MenuItem, Stack, TextField } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format, parseISO } from "date-fns";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppButton from "../../components/app-button/AppButton";
import AppDialog from "../../components/app-dialog/AppDialog";
import {
  addCustomer,
  getCustomers,
  searchCustomer,
  updateCustomer,
} from "../../reduxStore/slices/customerSlice";
import { useNotify } from "../../utils/snackbarUtils";

function CustomerDialog({ isDialogOpen, onDialogCancel, customerData }) {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const { loading: isLoading, searchResults } = useSelector(
    (state) => state.customer
  );
  const { user } = useSelector((state) => state.auth);
  const storeId = user?.storeId;

  // Form state
  const [id, setId] = useState(customerData?.id || "");
  const [uid, setUid] = useState(customerData?.uid || "");
  const [recordStatus, setRecordStatus] = useState(
    customerData?.record_status || ""
  );
  const [customerMobileNo, setCustomerMobileNo] = useState(
    customerData?.mobile_no || ""
  );
  const [firstName, setFirstName] = useState(customerData?.first_name || "");
  const [lastName, setLastName] = useState(customerData?.last_name || "");
  const [email, setEmail] = useState(customerData?.email || "");
  const [city, setCity] = useState(customerData?.city || "");
  const [address, setAddress] = useState(customerData?.address || "");
  const [dob, setDob] = useState(
    customerData.dob ? parseISO(customerData.dob) : null
  );
  const [gender, setGender] = useState(customerData?.gender || null);

  const formattedDob = dob ? format(dob, "yyyy-MM-dd") : null;

  const handleMobileChange = async (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setCustomerMobileNo(value);

    if (value.length === 10) {
      try {
        const res = await dispatch(
          searchCustomer({ mobileNo: value })
        ).unwrap();

        if (res && res.mobile_no && res.id !== id) {
          showError("Mobile number is already registered.");
        }
      } catch (err) {
        console.log("No customer found, can create new.");
      }
    }
  };

  const customerValidations = () => {
    if (!customerMobileNo) {
      showError("Please enter customer Mobile No.");
      return false;
    }

    if (customerMobileNo.length !== 10) {
      showError("Mobile number must be 10 digits.");
      return false;
    }

    if (!firstName) {
      showError("Please enter customer name.");
      return false;
    }
    return true;
  };

  // Save Customer (POST)
  const handleSaveCustomer = async () => {
    if (!customerValidations()) return;
    if (searchResults && searchResults.mobile_no) {
      showError("Mobile number is already registered.");
      return false;
    }
    try {
      const obj = {
        mobile_no: customerMobileNo,
        first_name: firstName,
        last_name: lastName,
        email,
        city,
        address,
        dob: formattedDob,
        gender,
      };

      const res = await dispatch(addCustomer(obj)).unwrap();
      await dispatch(getCustomers({ storeId }));
      onDialogCancel();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  // Update Customer (PATCH)
  const handleUpdateCustomer = async () => {
    if (!customerValidations()) return;
    if (searchResults && searchResults.mobile_no && searchResults.id !== id) {
      showError("Mobile number is already registered.");
      return false;
    }
    try {
      const obj = {
        id: id,
        uid: uid,
        mobile_no: customerMobileNo,
        first_name: firstName,
        last_name: lastName,
        email,
        city,
        address,
        dob: formattedDob,
        gender,
        record_status: recordStatus,
      };

      const res = await dispatch(updateCustomer(obj)).unwrap();
      await dispatch(getCustomers({ storeId }));
      onDialogCancel();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  // Dialog Footer
  const dialogAddCustomerFooter = (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Button size="medium" onClick={onDialogCancel}>
        Cancel
      </Button>
      <AppButton
        disabled={isLoading}
        isLoading={isLoading}
        fullWidth
        size="medium"
        variant="contained"
        onClick={uid ? handleUpdateCustomer : handleSaveCustomer}
      >
        {uid ? "Update" : "Save"}
      </AppButton>
    </Box>
  );

  return (
    <AppDialog
      maxWidth="sm"
      isOpen={isDialogOpen}
      id="add_customer_dialog"
      title={<>{uid ? "Edit Customer" : "Add Customer"}</>}
      footer={dialogAddCustomerFooter}
    >
      <Box sx={{ m: "20px 5px" }}>
        <Stack spacing={3} sx={{ mt: 2, mb: 2 }}>
          <TextField
            autoFocus
            placeholder="Enter customer Mobile No."
            label="Customer Mobile No. *"
            fullWidth
            autoComplete="T-customer-mobile"
            name="customer_mobile_no"
            type="number"
            value={customerMobileNo}
            onChange={handleMobileChange}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              autoComplete="T-first-name"
              label="First Name *"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
            />
            <TextField
              autoComplete="T-last-name"
              label="Last Name (Optional)"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              autoComplete="T-email"
              label="Email ID (Optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <TextField
              autoComplete="T-city"
              label="City (Optional)"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              fullWidth
            />
          </Box>
          <Stack spacing={2}>
            <TextField
              autoComplete="T-address"
              label="Address (Optional)"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date of Birth (Optional)"
                value={dob}
                onChange={(newValue) => setDob(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
            <TextField
              select
              fullWidth
              label="Gender (Optional)"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Stack>
        </Stack>
      </Box>
    </AppDialog>
  );
}

export default CustomerDialog;
